import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import remarkBreaks from 'remark-breaks';
import 'katex/dist/katex.min.css';
import DiagramRenderer from '../components/atoms/DiagramRenderer';

export const cleanLatexString = (str) => {
  if (typeof str !== 'string') return str;

    // Najpierw podziel tekst na segmenty: fragmenty matematyczne oraz resztę.
    // Ta reguła rozdziela ciąg na części zaczynające się od jednego lub dwóch dolarów,
    // kończące się analogicznie.
    const regexMath = /(\${1,2}[\s\S]+?\${1,2})/g;
    let segments = str.split(regexMath).map(segment => {
      if (segment.startsWith('$')) {
        // To jest fragment matematyczny.
        // Jeśli jest otoczony przez $$ (display math), pozostaw go bez zmian,
        // bo tam podwójne backslashe (np. w środowisku cases) muszą zostać.
        if (segment.startsWith('$$')) {
          // console.log(segment);
          // return segment.replace(/\\\\\\\\\n/g, '\\\\ ');
          return segment.replace(/\\\\(\r?\n)/g, '\\\\');

          return segment;
        } else {
          // Inline math – tutaj zamieniamy podwójne backslashe na pojedyncze.
          return segment.replace(/\\\\/g, '\\');
        }
      } else {
        // Fragment zwykły – zamień marker "\n" na znak nowej linii oraz inne sekwencje.
        return segment
          .replace(/\\n/g, '\n')
          .replace(/\\\\/g, '\\')
          .replace(/\\'/g, "'");
      }
    });
  
    let cleaned = segments.join('');
  
    // Upewnij się, że display math (blok $$ ... $$) jest oddzielony pustymi liniami
    // aby Markdown potraktował go jako osobny blok.
    // cleaned = cleaned.replace(/(\$\$[\s\S]+?\$\$)/g, '\n\n$1\n\n');
    // console.log(cleaned);
    return cleaned;
};

const formatSteps = (text) => {
    // 1) Zamień "Krok X:" -> "*Krok X:*\n"
    let formatted = text.replace(/(Krok \d+:)/g, '*$1*\n');
  
    // 2) Dla "Krok 2:" i kolejnych wstawiamy przed nimi pusty blok LaTeX
    //    (z \phantom{X}), który zrobi widoczną przerwę.
    formatted = formatted.replace(
      /\n\*Krok (\d+):\*/g,
      (match, stepNumber) => {
        // if (stepNumber === '1') {
        //   // Dla Krok 1 zostaje jak jest
        //   return match;
        // } else {
          // Dla Krok 2, 3, ... wstawiamy:
          //
          //   $$
          //   \phantom{X}
          //   $$
          //
          // a następnie *Krok X:* (czyli oryginalny tytuł kroku)
          return `
  $$
  
  $$
  
  *Krok ${stepNumber}:*`;
        
      }
    );
    formatted = formatted.replace(/\\(\s*[\r\n])/g, '$1');
    return formatted;
  };

const mdComponents = {
  h3: ({node, ...props}) => (
    <h3 className="text-[#233855] font-medium text-sm sm:text-base tracking-wider mt-4 mb-2 flex items-center gap-2" {...props} />
  ),
  blockquote: ({node, ...props}) => (
    <blockquote className="border-l-4 border-[#E09F2E] bg-gray-50 pl-3 py-2 my-3 rounded-r-md text-gray-700 italic shadow-sm" {...props} />
  ),
  p: ({node, ...props}) => (
    <p className="mb-2 last:mb-0 leading-relaxed" {...props} />
  ),
  ul: ({node, ...props}) => (
    <ul className="list-disc list-inside space-y-1 my-2 pl-2 marker:text-[#E09F2E]" {...props} />
  ),
  ol: ({node, ...props}) => (
    <ol className="list-decimal list-inside space-y-1 my-2 pl-2 marker:text-[#233855] marker:font-bold" {...props} />
  ),
  strong: ({node, ...props}) => (
    <strong className="font-bold text-gray-900" {...props} />
  ),
};

const splitPattern = /(\[JSXGRAPH\][\s\S]*?\[\/JSXGRAPH\]|<svg[\s\S]*?<\/svg>)/gi;

const EXAMPLE_MAX_CHARS = 500;

// Rysunki (JSXGraph / SVG) wypadają z zajawki w całości. Ich kod to setki znaków,
// które zjadłyby cały limit, a RenderMarkdownWithLatex dopasowuje wyłącznie
// DOMKNIĘTE bloki — przycięty `[JSXGRAPH]` bez zamknięcia leci na ekran jako
// surowy JavaScript. Do tego każdy rysunek to osobna plansza montowana per wiersz.
const DIAGRAM_BLOCK = /\[JSXGRAPH\][\s\S]*?\[\/JSXGRAPH\]|<svg[\s\S]*?<\/svg>/gi;
const DANGLING_DIAGRAM = /(\[JSXGRAPH\]|<svg)[\s\S]*$/i;

// Przykładowe zadanie to tylko zajawka — długie treści rozpychały arkusz działu.
// Cięcie „na sztywno" po N znakach potrafi rozciąć wyrażenie matematyczne, a
// niedomknięty $ / \( wywala KaTeX na całym bloku. Dlatego po przycięciu cofamy
// się do ostatniego miejsca, w którym delimitery są sparowane.
export const truncateExample = (text) => {
  if (typeof text !== 'string') return text;

  const withoutDiagrams = text
    .replace(DIAGRAM_BLOCK, '')
    .replace(DANGLING_DIAGRAM, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  if (withoutDiagrams.length <= EXAMPLE_MAX_CHARS) return withoutDiagrams;

  let cut = withoutDiagrams.slice(0, EXAMPLE_MAX_CHARS);

  // Nie tniemy w połowie słowa, o ile nie kosztuje to zbyt dużo treści.
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > EXAMPLE_MAX_CHARS * 0.6) cut = cut.slice(0, lastSpace);

  // Nieparzysta liczba niezescapowanych '$' = cięcie w środku wzoru.
  // Liczymy pętlą, bo lookbehind w regexie wywraca starsze WebView na iOS.
  let dollars = 0;
  for (let i = 0; i < cut.length; i++) {
    if (cut[i] === '$' && cut[i - 1] !== '\\') dollars += 1;
  }
  if (dollars % 2 === 1) cut = cut.slice(0, cut.lastIndexOf('$'));

  // To samo dla wariantu \( ... \).
  const open = (cut.match(/\\\(/g) || []).length;
  const close = (cut.match(/\\\)/g) || []).length;
  if (open > close) cut = cut.slice(0, cut.lastIndexOf('\\('));

  cut = cut.trimEnd();
  // Treść zaczynająca się od długiego wzoru zjadłaby się do zera — lepszy długi
  // przykład niż pusta ramka z samym wielokropkiem. Wracamy do wersji BEZ rysunków,
  // nie do oryginału — inaczej surowy kod planszy wróciłby na ekran.
  if (!cut) return withoutDiagrams;

  return `${cut}…`;
};

// Memoizowany po `text` — bez tego każdy re-render rodzica (np. tykający wskaźnik
// "myślę..." w czacie AI co 2.5s albo nowa wiadomość) re-typesetuje KaTeX we WSZYSTKICH
// bąbelkach naraz, co daje efekt migania. Zależy wyłącznie od `text`, więc memo jest bezpieczne.
export const RenderMarkdownWithLatex = React.memo(({ text }) => {
  if (typeof text !== 'string') return null;
  // Najpierw wydzielamy bloki JSXGRAPH/SVG — ich zawartość to surowy kod (JS/markup),
  // który trafia do new Function()/dangerouslySetInnerHTML. cleanLatexString wolno
  // uruchamiać WYŁĄCZNIE na częściach tekstowych, bo kolapsuje `\\` -> `\` i psuje
  // stringi w kodzie JSXGraph (np. '\\frac' -> '\frac', gdzie \f = form feed).
  const parts = text.split(splitPattern);

  return (
    <span>
      {parts.map((part, i) => {
        if (/^\[JSXGRAPH\]/i.test(part)) {
          const code = part.replace(/^\[JSXGRAPH\]\n?/i, '').replace(/\n?\[\/JSXGRAPH\]$/i, '');
          return <DiagramRenderer key={i} code={code} />;
        }
        if (/^<svg/i.test(part)) {
          return <span key={i} dangerouslySetInnerHTML={{ __html: part }} />;
        }
        const cleanPart = cleanLatexString(part).replace(/<br\s*\/?>/gi, '\n\n');
        return (
          <ReactMarkdown
            key={i}
            remarkPlugins={[remarkMath, remarkBreaks]}
            rehypePlugins={[rehypeKatex]}
            components={mdComponents}
          >
            {cleanPart}
          </ReactMarkdown>
        );
      })}
    </span>
  );
});
